import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { GuideEditHistoryEntry, guideEditorApi } from '../../services/guide-editor-api';
import { describeOp } from './model';

const SHOWN_OPS = 6;

function when(at: string): string {
  const d = new Date(at);
  return Number.isNaN(d.getTime()) ? at : format(d, 'd MMM yyyy, HH:mm');
}

/** Who changed what, and when. Read-only. Every value renders as text. */
export default function HistoryDrawer({
  open,
  slug,
  reloadKey,
  onClose,
}: {
  open: boolean;
  slug: string;
  /** Bumps when a save lands, so the list is fresh the next time it opens. */
  reloadKey: number;
  onClose: () => void;
}) {
  const [entries, setEntries] = useState<GuideEditHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEntries(null);
    setError(null);
    guideEditorApi
      .getEditHistory(slug)
      .then((h) => !cancelled && setEntries([...h.history].reverse()))
      .catch((err: any) => !cancelled && setError(err?.message || 'Failed to load the history'));
    return () => {
      cancelled = true;
    };
  }, [open, slug, reloadKey]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 2 }} data-testid="history-drawer">
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Edit history
          </Typography>
          <IconButton onClick={onClose} aria-label="Close history">
            <CloseIcon />
          </IconButton>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        {!entries && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {entries && entries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No edits recorded yet.
          </Typography>
        )}
        {entries && entries.length > 0 && (
          <List disablePadding>
            {entries.map((entry, i) => (
              <Box key={`${entry.at}-${i}`}>
                <ListItem sx={{ display: 'block', px: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {entry.editor}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                    {when(entry.at)}
                  </Typography>
                  {entry.ops.slice(0, SHOWN_OPS).map((op, j) => (
                    <Typography variant="body2" key={j} sx={{ wordBreak: 'break-word' }}>
                      {describeOp(op)}
                    </Typography>
                  ))}
                  {entry.ops.length > SHOWN_OPS && (
                    <Typography variant="caption" color="text.secondary">
                      and {entry.ops.length - SHOWN_OPS} more
                    </Typography>
                  )}
                </ListItem>
                {i < entries.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
}
